import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { formatInteger } from '@/lib/language';

export interface ScoreSparklinePoint {
  /** ISO-String, wie er aus `created_at` kommt. */
  date: string;
  score: number;
}

export interface ScoreSparklineProps {
  points: ScoreSparklinePoint[];
}

const VIEW_WIDTH = 100;
const VIEW_HEIGHT = 28;
/** Rand oben/unten in Score-Einheiten, damit die Linie nicht am Rand klebt. */
const Y_MARGIN = 2;

/**
 * Rein rechnerischer Kern der Skalierung, ohne React — so bleibt er einzeln
 * testbar und die Komponente selbst schlank.
 *
 * Bewusst NICHT hart auf [0, 100]: bei vier Werten zwischen 86 und 88 waere
 * eine 0-bis-100-Achse eine flache Linie, die eine Veraenderung behauptet zu
 * zeigen, obwohl es keine gibt (genau der Tufte-Verstoss, den diese
 * Komponente die recharts-Karte ersetzt). Skaliert wird stattdessen auf
 * [min, max] der Punkte selbst, mit etwas Rand oben und unten.
 *
 * Kehrseite, und die muss man kennen, bevor man diese Komponente irgendwo
 * verwendet: die Sparkline zeigt die FORM der Veraenderung, nicht ihre
 * GROESSE. Ein Ausschlag von 2 Punkten sieht hier genauso steil aus wie einer
 * von 20. Die Groesse tragen ausschliesslich die beiden Zahlen daneben (Start-
 * und aktueller Wert) — die Grafik allein darf dafuer nie herangezogen werden.
 */
export function scoreSparklinePath(scores: number[]): { x: number; y: number }[] {
  if (scores.length === 0) return [];
  if (scores.length === 1) {
    // Ein einzelner Punkt hat keine Ausdehnung in x — kommt hier praktisch nie
    // an (der Aufrufer zeigt unter zwei Punkten den Kurztext statt der
    // Grafik), bleibt aber definiert statt NaN zu erzeugen.
    return [{ x: 0, y: VIEW_HEIGHT / 2 }];
  }

  const min = Math.min(...scores);
  const max = Math.max(...scores);
  const range = max - min;

  return scores.map((score, index) => {
    const x = (index / (scores.length - 1)) * VIEW_WIDTH;
    if (range === 0) {
      // Identische Werte: eine waagerechte Linie in der Mitte statt einer
      // Division durch null.
      return { x, y: VIEW_HEIGHT / 2 };
    }
    const usableHeight = VIEW_HEIGHT - 2 * Y_MARGIN;
    // SVG-y waechst nach unten, ein hoeherer Score soll aber hoeher (kleineres
    // y) stehen — deshalb die Normalisierung invertiert.
    const normalized = (score - min) / range;
    const y = Y_MARGIN + (1 - normalized) * usableHeight;
    return { x, y };
  });
}

/**
 * Score-Verlauf als Sparkline (Spec §5.3, "Zeile 3"). Ersetzt die bisherige
 * recharts-Karte: 4 Datenpunkte, 5 Gitterlinien und eine 0-bis-100-Achse fuer
 * eine praktisch flache Linie waren mehr Diagramm, als die Datenlage
 * hergab (UX-Audit 09/2026, Tufte-Verstoss).
 *
 * Keine Achsen, kein Gitter, kein Tooltip, kein Interaktions-Aufwand — die
 * Aussage steht als Zahl daneben, die Linie liefert nur die Richtung.
 */
export function ScoreSparkline({ points }: ScoreSparklineProps) {
  const { t, i18n } = useTranslation('optimization');
  const language = i18n?.language;

  const dateLocale = language === 'de' ? 'de-DE' : 'en-US';

  if (points.length < 2) {
    return (
      <Card
        data-testid="score-trend"
        style={{ border: '1px solid hsl(var(--primary) / 0.08)', boxShadow: 'var(--shadow-sm)' }}
      >
        <CardContent className="pt-6 pb-6">
          <h2 className="text-sm font-heading font-bold">{t('feedCheck.trend.title')}</h2>
          <p data-testid="trend-too-short" className="mt-2 text-xs text-muted-foreground">
            {t('feedCheck.trend.tooShort')}
          </p>
        </CardContent>
      </Card>
    );
  }

  const first = points[0];
  const last = points[points.length - 1];
  const scores = points.map((p) => p.score);
  const coords = scoreSparklinePath(scores);
  const polylinePoints = coords.map((c) => `${c.x},${c.y}`).join(' ');
  const lastCoord = coords[coords.length - 1];

  const firstDate = new Date(first.date).toLocaleDateString(dateLocale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  return (
    <Card
      data-testid="score-trend"
      style={{ border: '1px solid hsl(var(--primary) / 0.08)', boxShadow: 'var(--shadow-sm)' }}
    >
      <CardContent className="pt-6 pb-6 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-sm font-heading font-bold">{t('feedCheck.trend.title')}</h2>
          <div className="text-right">
            <span className="block text-2xl font-heading font-bold tabular-nums">
              {formatInteger(last.score, language)}
            </span>
            <span className="block text-[11px] text-muted-foreground">
              {t('feedCheck.trend.from', { score: formatInteger(first.score, language), date: firstDate })}
            </span>
          </div>
        </div>

        {/* Die Linie traegt keine eigene Information fuer Screenreader — die
            steht als sr-only-Zusammenfassung daneben (keine Information darf
            nur in der Grafik stecken). */}
        <svg
          viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
          preserveAspectRatio="none"
          className="w-full h-7"
          aria-hidden="true"
        >
          <polyline
            points={polylinePoints}
            fill="none"
            stroke="hsl(var(--primary))"
            strokeWidth="1.5"
            vectorEffect="non-scaling-stroke"
          />
          <circle cx={lastCoord.x} cy={lastCoord.y} r={2} fill="hsl(var(--primary))" />
        </svg>

        {/*
          Keine eigene Locale-Zeile: diese Zusammenfassung ist keine neue
          Aussage, sondern die reine Wiederholung der beiden Zahlen, die
          ohnehin sichtbar oben stehen (Titel + `feedCheck.trend.from`). Eine
          eigene Copy-Zeile dafuer anzulegen waere eine dritte Formulierung
          fuer dieselben zwei Zahlen — und diese Aufgabe darf keine
          Locale-Datei anfassen. Der Pfeil ist ein Symbol, kein Gedankenstrich.
        */}
        <span className="sr-only">
          {t('feedCheck.trend.title')}: {formatInteger(first.score, language)} {'→'}{' '}
          {formatInteger(last.score, language)}
        </span>
      </CardContent>
    </Card>
  );
}
